import { useEffect, useState } from "react";
import { Heading, Subheading } from "../../design/heading";
import CreateKeyButton from "./components/CreateKeyButton";
import { Key } from "../../models/Key";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import { Divider } from "../../design/divider";
import { Button } from "../../design/button";
import {
  KeyIcon,
  ClipboardDocumentIcon,
  ShieldCheckIcon,
} from "@heroicons/react/20/solid";

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<Key[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    const docRef = query(
      collection(db, "keys"),
      where("userId", "==", user?.uid),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const items = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Key,
        );
        setItems(items);
      },
      (err) => {
        console.error(err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-8 gap-4">
          <div>
            <Heading>API Keys</Heading>
            <Text className="mt-2 text-gray-600 dark:text-gray-400">
              Generate and manage authentication keys for API access
            </Text>
          </div>
          <CreateKeyButton />
        </div>

        {/* Security Notice */}
        <div className="p-4 mb-6 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <Text className="font-semibold text-blue-900 dark:text-blue-100">
                Keep your keys secure
              </Text>
              <Text className="text-sm text-blue-700 dark:text-blue-300">
                Never share your API keys publicly. Treat them like passwords and store them securely.
              </Text>
            </div>
          </div>
        </div>

        {/* API Keys List */}
        {items.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-lg shadow-md dark:bg-zinc-900">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gray-100 rounded-full dark:bg-zinc-800">
                <KeyIcon className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <Subheading className="mb-2">No API keys yet</Subheading>
            <Text className="mb-6 text-gray-600 dark:text-gray-400">
              Create your first API key to start using the platform
            </Text>
            <CreateKeyButton />
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                      <KeyIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Subheading>API Key</Subheading>
                        <Badge color="blue">Active</Badge>
                      </div>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        ID: {item.id}
                      </Text>
                    </div>
                  </div>
                </div>

                <Divider className="my-4" />

                <div>
                  <Text className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Secret Key
                  </Text>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 font-mono text-sm bg-gray-50 dark:bg-zinc-800 rounded-lg overflow-x-auto">
                      <code className="text-gray-900 dark:text-gray-100">
                        {item.token}
                      </code>
                    </div>
                    <Button
                      outline
                      onClick={() => copyToClipboard(item.token, item.id)}
                      className="shrink-0"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
                      {copiedId === item.id ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-zinc-700">
                  <div>
                    <Text className="text-xs text-gray-500">Created</Text>
                    <Text className="text-sm font-medium">
                      {item.createdAt ? item.createdAt.toDate().toLocaleDateString() : "N/A"}
                    </Text>
                  </div>
                  <div>
                    <Text className="text-xs text-gray-500">Last Used</Text>
                    <Text className="text-sm font-medium">
                      {item.lastUsed ? item.lastUsed.toDate().toLocaleDateString() : "Never"}
                    </Text>
                  </div>
                  <div>
                    <Text className="text-xs text-gray-500">Requests</Text>
                    <Text className="text-sm font-medium">
                      {item.requestCount?.toLocaleString() ?? "0"}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Usage Guide */}
        <div className="p-6 mt-8 bg-white rounded-lg shadow-md dark:bg-zinc-900">
          <Subheading className="mb-4">Quick Start</Subheading>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 dark:bg-zinc-800 rounded-lg">
              <Text className="mb-1 text-sm font-semibold">1. Copy your API key</Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Click the copy button next to your key above
              </Text>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-zinc-800 rounded-lg">
              <Text className="mb-1 text-sm font-semibold">2. Add to your requests</Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Include in the Authorization header: <code className="px-1 py-0.5 bg-gray-200 dark:bg-zinc-700 rounded">Bearer YOUR_API_KEY</code>
              </Text>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-zinc-800 rounded-lg">
              <Text className="mb-1 text-sm font-semibold">3. Start building</Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Check out the <a href="/docs" className="text-blue-600 dark:text-blue-400 hover:underline">API documentation</a> for examples
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DashboardPage;
